/**
 * Idle pr-* previews are frozen (every ECS service at zero) after an hour with
 * no traffic. Preview builds carry a thaw URL so the frontend, which stays up
 * on CloudFront, can show a splash and wake the api. Permanent stages never
 * set these variables, so everything here is inert outside previews.
 */

/** Dispatched on window when an api call fails in a way a frozen preview does. */
export const PREVIEW_API_UNREACHABLE_EVENT = 'roger:preview-api-unreachable';

export interface PreviewWakeConfig {
  stage: string;
  thawUrl: string;
}

export function previewWakeConfig(): PreviewWakeConfig | null {
  const stage = (
    import.meta.env.VITE_PREVIEW_STAGE as string | undefined
  )?.trim();
  const thawUrl = (
    import.meta.env.VITE_PREVIEW_THAW_URL as string | undefined
  )?.trim();
  return stage && thawUrl ? { stage, thawUrl } : null;
}

/**
 * A frozen preview's api target group has no targets, so the load balancer
 * answers 503 without CORS headers and the browser reports a network error.
 * 502 and 504 cover a task that is still starting.
 */
export function looksFrozen(status: number | undefined): boolean {
  return (
    status === undefined || status === 502 || status === 503 || status === 504
  );
}

export function reportPreviewApiUnreachable(status: number | undefined): void {
  if (previewWakeConfig() && looksFrozen(status)) {
    window.dispatchEvent(new Event(PREVIEW_API_UNREACHABLE_EVENT));
  }
}

export interface PreviewServiceStatus {
  name: string;
  desired: number;
  running: number;
}

export type ServiceState = 'waiting' | 'starting' | 'running';

export function serviceState(service: PreviewServiceStatus): ServiceState {
  if (service.desired > 0 && service.running >= service.desired) {
    return 'running';
  }
  return service.desired > 0 ? 'starting' : 'waiting';
}

export function previewHealthUrl(): string {
  return `${import.meta.env.VITE_API_URL || ''}/health`;
}
